import { useState } from 'react';
import { useFeedback } from '../common/Feedback';
import { Modal } from '../common/Modal';
import { X, Pencil, Archive, Plus } from 'lucide-react';
import { Holder, formatarOab } from '../../utils/holders';
import { HolderPayload } from '../../hooks/useHolders';

const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

interface HolderManagerProps {
  holders: Holder[];
  /** D23: abre já editando este titular (lápis ao lado do nome da pasta aberta). */
  editingHolderId?: string | null;
  onCreate: (payload: HolderPayload) => Promise<void>;
  onUpdate: (id: string, payload: HolderPayload) => Promise<void>;
  onArchive: (id: string) => Promise<void>;
  onClose: () => void;
}

const inputStyle: React.CSSProperties = { background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' };
const iconButton: React.CSSProperties = { background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', padding: 'var(--space-2)', borderRadius: 'var(--radius-md)', cursor: 'pointer', display: 'flex', alignItems: 'center' };

const VAZIO = { name: '', oab_number: '', oab_uf: '' };

// Modal "Gerenciar titulares" (owner/admin): criar, editar e arquivar.
// Titular é o advogado responsável por uma pasta do cofre; não precisa ter login e não dá acesso a nada.
export function HolderManager({ holders, editingHolderId = null, onCreate, onUpdate, onArchive, onClose }: HolderManagerProps) {
  const { confirmar } = useFeedback();
  const titularInicial = editingHolderId ? holders.find((holder) => holder.id === editingHolderId) ?? null : null;
  const [form, setForm] = useState(() =>
    titularInicial ? { name: titularInicial.name, oab_number: titularInicial.oab_number || '', oab_uf: titularInicial.oab_uf || '' } : VAZIO
  );
  const [editingId, setEditingId] = useState<string | null>(titularInicial?.id ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = (holder: Holder) => {
    setEditingId(holder.id);
    setForm({ name: holder.name, oab_number: holder.oab_number || '', oab_uf: holder.oab_uf || '' });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(VAZIO);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numero = form.oab_number.replace(/[.\s]/g, '');
    if (!form.name.trim()) {
      setError('Informe o nome do titular.');
      return;
    }
    if (numero && !/^\d{1,8}$/.test(numero)) {
      setError('O número da OAB deve ter só números.');
      return;
    }
    if (!!numero !== !!form.oab_uf) {
      setError('Informe o número e a UF da OAB juntos (ou deixe os dois vazios).');
      return;
    }

    const payload: HolderPayload = { name: form.name.trim(), oab_number: numero || null, oab_uf: form.oab_uf || null };
    setIsSubmitting(true);
    setError(null);
    try {
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onCreate(payload);
      }
      cancelEdit();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar titular');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchive = async (holder: Holder) => {
    if (!(await confirmar({ titulo: `Arquivar o titular "${holder.name}"?`, mensagem: 'A pasta deixa de aparecer. Só é possível arquivar titulares sem credenciais.', confirmar: 'Arquivar' }))) return;
    setError(null);
    try {
      await onArchive(holder.id);
      if (editingId === holder.id) cancelEdit();
    } catch (err: any) {
      setError(err.message || 'Erro ao arquivar titular');
    }
  };

  return (
    <Modal pedirParaFechar={onClose} protegerDigitacao>
      <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: '560px', padding: 'var(--space-8)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>Gerenciar titulares</h2>
          <button type="button" onClick={onClose} aria-label="Fechar" title="Fechar" className="botao-icone botao-icone--sem-borda">
            <X size={24} />
          </button>
        </div>

        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5, marginBottom: 'var(--space-4)' }}>
          Titular é o advogado responsável pelas credenciais de uma pasta. Não precisa ter login no Lictor.
          Ele só organiza a tela: quem pode usar cada credencial continua sendo definido em <strong>Grupos</strong>.
        </p>

        {error && (
          <div style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
          <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            {editingId ? 'Editar titular' : 'Novo titular'}
          </label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome (ex.: Dra. Fulana de Tal)" maxLength={200} style={inputStyle} />
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <input value={form.oab_number} onChange={(e) => setForm({ ...form, oab_number: e.target.value })} placeholder="Nº da OAB (opcional)" inputMode="numeric" style={{ ...inputStyle, flex: 1 }} />
            <select value={form.oab_uf} onChange={(e) => setForm({ ...form, oab_uf: e.target.value })} style={{ ...inputStyle, width: '96px' }}>
              <option value="">UF</option>
              {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            {editingId && (
              <button type="button" onClick={cancelEdit} style={{ flex: 1, padding: 'var(--space-3)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}>
                Cancelar edição
              </button>
            )}
            <button type="submit" disabled={isSubmitting} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', padding: 'var(--space-3)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontWeight: 'var(--font-medium)', opacity: isSubmitting ? 0.6 : 1 }}>
              {editingId ? 'Salvar' : <><Plus size={16} /> Adicionar titular</>}
            </button>
          </div>
        </form>

        {holders.length === 0 ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>Nenhum titular cadastrado ainda.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {holders.map((holder) => (
              <div key={holder.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-md)', background: editingId === holder.id ? 'rgba(26, 154, 102, 0.06)' : 'transparent' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)', overflowWrap: 'anywhere' }}>{holder.name}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                    {[formatarOab(holder), `${holder.credential_count} credencia${holder.credential_count === 1 ? 'l' : 'is'}`].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button type="button" onClick={() => startEdit(holder)} title="Editar" style={iconButton}><Pencil size={16} /></button>
                <button type="button" onClick={() => handleArchive(holder)} title="Arquivar" style={iconButton}><Archive size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
